import { useState, useRef, useMemo } from 'react';
import {
  Upload,
  FileSpreadsheet,
  AlertTriangle,
  AlertCircle,
  Check,
  Loader2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Select } from '../ui/select-custom';
import {
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogCloseButton,
  DialogFooter,
} from '../ui/dialog';
import { TableShell, Th, Td } from '../investments/investment-tables';
import { readB3File, buildImportPlan, B3ImportError } from '../../lib/b3-import';
import type { B3ImportPlan, B3NewAsset, B3ParsedEarning } from '../../lib/b3-import';
import { XlsxError } from '../../lib/xlsx';
import { formatCurrency, formatQuantity } from '../../lib/format';
import { earningKinds, investmentTypes } from '../../types';
import type { Earning, EarningKind, InvestmentAsset, InvestmentType, Trade } from '../../types';

export interface ImportB3DialogProps {
  open: boolean;
  onClose: () => void;
  assets: InvestmentAsset[];
  trades: Trade[];
  earnings: Earning[];
  onImport: (input: {
    newAssets: B3NewAsset[];
    trades: (Omit<Trade, 'id' | 'userId' | 'createdAt'> & { ticker: string })[];
    earnings: (Omit<Earning, 'id' | 'userId' | 'createdAt'> & { ticker: string })[];
  }) => Promise<{ assets: number; trades: number; earnings: number }>;
}

interface Summary {
  assets: number;
  trades: number;
  earnings: number;
}

function Collapsible({
  title,
  count,
  tone = 'neutral',
  children,
  defaultOpen = false,
}: {
  title: string;
  count: number;
  tone?: 'neutral' | 'muted';
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (count === 0) return null;

  return (
    <div className="rounded-lg border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
      >
        {open ? (
          <ChevronDown className="size-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-4 text-muted-foreground" />
        )}
        <span className={tone === 'muted' ? 'text-muted-foreground' : 'text-foreground'}>
          {title}
        </span>
        <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
          {count}
        </span>
      </button>
      {open && <div className="border-t border-border px-3 py-2">{children}</div>}
    </div>
  );
}

export function ImportB3Dialog({
  open,
  onClose,
  assets,
  trades,
  earnings,
  onImport,
}: ImportB3DialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [plan, setPlan] = useState<B3ImportPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState<Summary | null>(null);

  /** Tipo escolhido pelo usuário quando a detecção automática erra. */
  const [typeOverrides, setTypeOverrides] = useState<Record<string, InvestmentType>>({});

  const newTrades = useMemo(() => plan?.trades.filter((t) => !t.duplicate) ?? [], [plan]);
  const inferredTrades = useMemo(() => newTrades.filter((t) => t.inferred), [newTrades]);
  const newEarnings = useMemo(
    () => plan?.earnings.filter((e) => !e.duplicate) ?? [],
    [plan]
  );
  const duplicateCount =
    (plan?.trades.filter((t) => t.duplicate).length ?? 0) +
    (plan?.earnings.filter((e) => e.duplicate).length ?? 0);

  // Só cadastra papel que aparece em algo que vai ser importado de fato.
  const assetsToCreate = useMemo(() => {
    if (!plan) return [];
    const needed = new Set([
      ...newTrades.map((t) => t.ticker),
      ...newEarnings.map((e) => e.ticker),
    ]);
    return plan.newAssets
      .filter((asset) => needed.has(asset.ticker))
      .map((asset) => ({ ...asset, type: typeOverrides[asset.ticker] ?? asset.type }));
  }, [plan, newTrades, newEarnings, typeOverrides]);

  /**
   * Rendimento e dividendo são a mesma isenção, mas vão em fichas diferentes
   * do IRPF — então o tipo do papel precisa mandar no tipo do provento.
   */
  const reclassify = (earning: B3ParsedEarning): EarningKind => {
    const type = typeOverrides[earning.ticker] ?? earning.assetType;
    if (type === 'acao' && earning.kind === 'rendimento_fii') return 'dividendo';
    if (type === 'fii' && earning.kind === 'dividendo') return 'rendimento_fii';
    return earning.kind;
  };

  const reset = () => {
    setFileName(null);
    setPlan(null);
    setError(null);
    setDone(null);
    setTypeOverrides({});
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (file: File) => {
    setParsing(true);
    setError(null);
    setPlan(null);
    setDone(null);
    setFileName(file.name);

    try {
      const rows = await readB3File(file);
      setPlan(buildImportPlan(rows, { assets, trades, earnings }));
    } catch (err) {
      if (err instanceof B3ImportError || err instanceof XlsxError) {
        setError(err.message);
      } else {
        console.error('Erro ao ler arquivo da B3:', err);
        setError('Não consegui ler o arquivo. Confira se é o relatório de Movimentação da B3.');
      }
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    if (!plan) return;
    setImporting(true);
    setError(null);

    try {
      // O tipo escolhido na prévia manda, inclusive no papel recém-criado.

      const result = await onImport({
        newAssets: assetsToCreate,
        trades: newTrades.map((trade) => ({
          ticker: trade.ticker,
          assetId: '',
          operation: trade.operation,
          date: trade.date,
          quantity: trade.quantity,
          unitPrice: trade.unitPrice,
          fees: 0,
          irrfWithheld: 0,
          notes: trade.inferred
            ? 'Subscrição exercida — preço não vem no extrato da B3, preencher'
            : 'Importado da B3',
        })),
        earnings: newEarnings.map((earning) => ({
          ticker: earning.ticker,
          assetId: '',
          // Se o usuário reclassificou o papel na prévia, o provento segue junto.
          // Sem override vale o tipo do plano, que já olha o papel cadastrado.
          kind: reclassify(earning),
          paymentDate: earning.paymentDate,
          quantityBase: earning.quantityBase,
          valuePerShare: earning.valuePerShare,
          grossValue: earning.grossValue,
          irrfWithheld: earning.irrfWithheld,
        })),
      });
      setDone(result);
      setPlan(null);
    } catch {
      setError('Falha ao gravar. Nada foi importado pela metade — tente de novo.');
    } finally {
      setImporting(false);
    }
  };

  const nothingToImport =
    plan !== null && newTrades.length === 0 && newEarnings.length === 0;

  return (
    <DialogRoot
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogPortal>
        <DialogBackdrop className="z-40" />
        <DialogPopup className="z-41 max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Importar extrato da B3</DialogTitle>
            <DialogCloseButton />
          </DialogHeader>

          <DialogContent>
            {!plan && !done && (
              <>
                <p className="text-sm text-foreground-subtle">
                  Use o relatório de <strong>Movimentação</strong> da área do investidor da
                  B3 (Extratos e Informativos → Movimentação), em .xlsx ou .csv. Operações e
                  proventos já cadastrados não entram de novo.
                </p>

                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  disabled={parsing}
                  className="flex w-full flex-col items-center gap-2 rounded-xl border border-dashed border-input px-4 py-8 text-center transition-colors hover:border-primary hover:bg-muted disabled:opacity-60"
                >
                  {parsing ? (
                    <Loader2 className="size-6 animate-spin text-muted-foreground" />
                  ) : (
                    <Upload className="size-6 text-muted-foreground" />
                  )}
                  <span className="text-sm font-medium text-foreground">
                    {parsing ? 'Lendo arquivo...' : 'Escolher arquivo'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {fileName ?? '.xlsx ou .csv'}
                  </span>
                </button>

                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.csv"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFile(file);
                  }}
                />
              </>
            )}

            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {done && (
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-3 text-sm text-green-900">
                  <Check className="mt-0.5 size-4 shrink-0" />
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium">Importação concluída.</span>
                    <span className="text-xs">
                      {done.assets} {done.assets === 1 ? 'papel cadastrado' : 'papéis cadastrados'},{' '}
                      {done.trades} {done.trades === 1 ? 'operação' : 'operações'} e {done.earnings}{' '}
                      {done.earnings === 1 ? 'provento' : 'proventos'}.
                    </span>
                  </div>
                </div>
                {done.assets > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Os papéis novos entraram sem CNPJ e sem cotação. Preencha o CNPJ de cada
                    um (o IRPF exige) e clique em “Cotações” para buscar os preços.
                  </p>
                )}
              </div>
            )}

            {plan && (
              <div className="flex flex-col gap-3">
                {/* Resumo */}
                <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm">
                  <FileSpreadsheet className="size-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-xs text-foreground-subtle">{fileName}</span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Operações', value: newTrades.length },
                    { label: 'Proventos', value: newEarnings.length },
                    { label: 'Papéis novos', value: assetsToCreate.length },
                  ].map((tile) => (
                    <div
                      key={tile.label}
                      className="flex flex-col gap-0.5 rounded-lg border border-border px-3 py-2"
                    >
                      <span className="text-lg font-semibold text-foreground">{tile.value}</span>
                      <span className="text-xs text-muted-foreground">{tile.label}</span>
                    </div>
                  ))}
                </div>

                {duplicateCount > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {duplicateCount}{' '}
                    {duplicateCount === 1 ? 'lançamento já estava' : 'lançamentos já estavam'}{' '}
                    cadastrados e {duplicateCount === 1 ? 'será ignorado' : 'serão ignorados'}.
                  </p>
                )}

                {nothingToImport && (
                  <p className="rounded-lg bg-muted px-3 py-2 text-sm text-foreground-subtle">
                    Nada novo nesse arquivo — tudo já está cadastrado.
                  </p>
                )}

                {/* Sem corretagem no relatório */}
                {newTrades.length > 0 && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                    <span>
                      O relatório de Movimentação não traz corretagem nem emolumentos, então as
                      operações entram com taxas zeradas. Se você paga corretagem, ajuste as
                      operações depois — as taxas entram no custo médio e mudam o IRPF.
                    </span>
                  </div>
                )}

                {/* Entradas deduzidas de evento societário */}
                {inferredTrades.length > 0 && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">
                        {inferredTrades.length}{' '}
                        {inferredTrades.length === 1
                          ? 'entrada foi deduzida'
                          : 'entradas foram deduzidas'}{' '}
                        de subscrição exercida.
                      </span>
                      {inferredTrades.map((trade) => (
                        <span key={trade.line}>
                          <strong>{trade.ticker}</strong>: +{formatQuantity(trade.quantity)} cotas em{' '}
                          {trade.date.toLocaleDateString('pt-BR')}
                        </span>
                      ))}
                      <span>
                        Sem elas a posição ficaria negativa. Entram{' '}
                        <strong>com preço zero</strong> — o extrato da B3 não traz o valor
                        pago na subscrição. Edite o preço em Operações; até lá o custo
                        médio desses papéis fica subestimado.
                      </span>
                    </div>
                  </div>
                )}

                {/* Posição negativa: falta alguma entrada no histórico */}
                {plan.negativePositions.length > 0 && (
                  <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                    <AlertCircle className="mt-0.5 size-4 shrink-0" />
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">
                        Alguns papéis ficam com quantidade negativa.
                      </span>
                      {plan.negativePositions.map((item) => (
                        <span key={item.ticker}>
                          <strong>{item.ticker}</strong>: faltam {item.shortfall} cotas em{' '}
                          {item.date.toLocaleDateString('pt-BR')}.
                        </span>
                      ))}
                      <span>
                        Não achei no arquivo nenhum evento que explicasse o rombo. Procure a
                        subscrição, incorporação ou troca de ticker que faltou e lance a
                        entrada à mão.
                      </span>
                    </div>
                  </div>
                )}

                {/* Eventos societários: entram só por sua decisão */}
                <Collapsible
                  title="Eventos que precisam de você"
                  count={plan.events.length}
                  defaultOpen
                >
                  <p className="mb-2 text-xs text-muted-foreground">
                    Estas linhas mexem na posição mas o relatório não traz preço, ou o efeito
                    é ambíguo. Elas <strong>não</strong> são importadas: confira cada uma e
                    lance à mão o que for real.
                  </p>
                  <TableShell>
                    <thead>
                      <tr>
                        <Th>Data</Th>
                        <Th>Papel</Th>
                        <Th>Movimentação</Th>
                        <Th align="right">Qtd</Th>
                        <Th align="right">Valor</Th>
                        <Th>O que costuma ser</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.events.map((event) => (
                        <tr key={event.line}>
                          <Td>{event.date.toLocaleDateString('pt-BR')}</Td>
                          <Td className="font-medium text-foreground">{event.ticker}</Td>
                          <Td className="max-w-[160px] whitespace-normal text-xs text-foreground-subtle">
                            {event.movement}
                          </Td>
                          <Td align="right">
                            {event.quantity ? formatQuantity(event.quantity) : '—'}
                          </Td>
                          <Td align="right">
                            {event.total > 0 ? (
                              `R$ ${formatCurrency(event.total)}`
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </Td>
                          <Td className="max-w-[280px] whitespace-normal text-xs text-muted-foreground">
                            {event.explanation}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </TableShell>
                </Collapsible>

                {/* Papéis novos, com tipo editável */}
                <Collapsible
                  title="Papéis que serão cadastrados"
                  count={assetsToCreate.length}
                  defaultOpen
                >
                  <TableShell>
                    <thead>
                      <tr>
                        <Th>Papel</Th>
                        <Th>Nome</Th>
                        <Th>Tipo</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {assetsToCreate.map((asset) => (
                        <tr key={asset.ticker}>
                          <Td className="font-medium text-foreground">{asset.ticker}</Td>
                          <Td className="max-w-[260px] whitespace-normal text-xs text-foreground-subtle">
                            {asset.name}
                          </Td>
                          <Td>
                            <Select
                              value={asset.type}
                              onChange={(value) =>
                                setTypeOverrides((prev) => ({
                                  ...prev,
                                  [asset.ticker]: value as InvestmentType,
                                }))
                              }
                              options={(Object.keys(investmentTypes) as InvestmentType[]).map(
                                (key) => ({
                                  value: key,
                                  label: investmentTypes[key].shortLabel,
                                })
                              )}
                              className="h-7 text-xs"
                            />
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </TableShell>
                  <p className="mt-2 text-xs text-muted-foreground">
                    O tipo é deduzido do nome do produto. Confira os que terminam em 11: units
                    como BPAC11 e TAEE11 são ações, não FII.
                  </p>
                </Collapsible>

                <Collapsible title="Operações" count={newTrades.length}>
                  <TableShell>
                    <thead>
                      <tr>
                        <Th>Data</Th>
                        <Th>Papel</Th>
                        <Th>Operação</Th>
                        <Th align="right">Qtd</Th>
                        <Th align="right">Valor unit.</Th>
                        <Th align="right">Total</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {newTrades.map((trade) => (
                        <tr key={trade.key + trade.line}>
                          <Td>{trade.date.toLocaleDateString('pt-BR')}</Td>
                          <Td className="font-medium text-foreground">{trade.ticker}</Td>
                          <Td>
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                trade.operation === 'buy'
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'bg-amber-50 text-amber-700'
                              }`}
                            >
                              {trade.operation === 'buy' ? 'Compra' : 'Venda'}
                            </span>
                          </Td>
                          <Td align="right">{formatQuantity(trade.quantity)}</Td>
                          <Td align="right">
                            {trade.inferred ? (
                              <span className="text-amber-700">a preencher</span>
                            ) : (
                              `R$ ${formatCurrency(trade.unitPrice)}`
                            )}
                          </Td>
                          <Td align="right">
                            {trade.inferred ? (
                              <span className="text-xs text-muted-foreground">deduzida</span>
                            ) : (
                              `R$ ${formatCurrency(trade.total)}`
                            )}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </TableShell>
                </Collapsible>

                <Collapsible title="Proventos" count={newEarnings.length}>
                  <TableShell>
                    <thead>
                      <tr>
                        <Th>Pagamento</Th>
                        <Th>Papel</Th>
                        <Th>Tipo</Th>
                        <Th align="right">Bruto</Th>
                        <Th align="right">IRRF</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {newEarnings.map((earning) => (
                        <tr key={earning.key + earning.line}>
                          <Td>{earning.paymentDate.toLocaleDateString('pt-BR')}</Td>
                          <Td className="font-medium text-foreground">{earning.ticker}</Td>
                          <Td className="text-xs text-foreground-subtle">
                            {earningKinds[earning.kind]?.label}
                          </Td>
                          <Td align="right">R$ {formatCurrency(earning.grossValue)}</Td>
                          <Td align="right">
                            {earning.irrfWithheld > 0 ? (
                              <span className="text-red-600">
                                -R$ {formatCurrency(earning.irrfWithheld)}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </TableShell>
                </Collapsible>

                <Collapsible
                  title="Linhas ignoradas"
                  count={plan.skipped.length}
                  tone="muted"
                >
                  <TableShell>
                    <thead>
                      <tr>
                        <Th>Produto</Th>
                        <Th>Movimentação</Th>
                        <Th>Motivo</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.skipped.map((row) => (
                        <tr key={row.line}>
                          <Td className="max-w-[200px] whitespace-normal text-xs">
                            {row.product}
                          </Td>
                          <Td className="max-w-[180px] whitespace-normal text-xs text-foreground-subtle">
                            {row.movement}
                          </Td>
                          <Td className="text-xs text-muted-foreground">{row.reason}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </TableShell>
                </Collapsible>
              </div>
            )}
          </DialogContent>

          <DialogFooter>
            {done ? (
              <>
                <Button variant="outline" onClick={reset}>
                  Importar outro
                </Button>
                <Button onClick={handleClose}>Fechar</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={handleClose} disabled={importing}>
                  Cancelar
                </Button>
                {plan && (
                  <Button onClick={handleImport} disabled={importing || nothingToImport}>
                    {importing && <Loader2 className="size-4 animate-spin" />}
                    {importing
                      ? 'Importando...'
                      : `Importar ${newTrades.length + newEarnings.length} lançamentos`}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogPopup>
      </DialogPortal>
    </DialogRoot>
  );
}
